// UI11: Floating Properties Panel
// Contextual panel near selected objects for editing properties.
// Shell + field registry pattern: shell handles positioning/visibility,
// each sourceType registers its own field renderer.

import type { GardenScene } from '@/scene/GardenScene'
import type { SelectionResult, MultiSelectDelta } from '@/scene/SelectionManager'
import type { EditableShape } from '@/types/editor'
import type { Zone, ContextPin } from '@/types/zone'
// GardenObject accessed via gardenObjectState.getObjectById()
import { ZoneAdapter } from '@/editor/adapters/ZoneAdapter'
import { GroundShapeAdapter } from '@/editor/adapters/GroundShapeAdapter'
import { BuildingAdapter } from '@/editor/adapters/BuildingAdapter'
import { PlantAdapter } from '@/editor/adapters/PlantAdapter'
import { PinAdapter } from '@/editor/adapters/PinAdapter'
import { BoundaryAdapter } from '@/editor/adapters/BoundaryAdapter'
import { getViewer } from '@/ui/state'
import { calculateAlignmentAngle } from '@/geodata/boundaryImport'
import { zoneState } from '@/zones/ZoneState'
import { gardenObjectState } from '@/state/GardenObjectState'
import { undoStack } from '@/editor/UndoStack'
import { getZoneTypeDef, getPinTypeDef } from '@/constants/zoneTypes'
import { DPM_SCALE } from '@/constants/coordinates'
import { calculateRectArea, calculateEllipseArea, calculatePolygonAreaFromPoints, formatArea } from '@/utils/areaCalculation'
import { generatePathPolygon } from '@/utils/pathGeometry'
import { t, getCurrentLocale } from '@/i18n'
import { debug } from '@/utils/debug'
import { isObjectLocked, setObjectLocked } from '@/utils/lockState'
import './FloatingPropsPanel.css'

/** Map object.type to adapter sourceType */
function objectTypeToSourceType(type: string): string {
  if (type === 'rectangle' || type === 'polygon' || type === 'ellipse' || type === 'path') return 'groundShape'
  return type
}

// Accent colors per source type (matches adapter getAccentColor values)
const ACCENT_COLORS: Record<string, string> = {
  zone: '#4ecca3',
  groundShape: '#f76d33',
  building: '#FFBA1A',
  plant: '#92cc09',
  pin: '#9b59b6',
  boundary: '#ff0000',
}

interface PanelState {
  shape: EditableShape | null
  visible: boolean
}

type FieldRenderer = (shape: EditableShape, container: HTMLElement, panel: FloatingPropsPanel) => void

export class FloatingPropsPanel {
  private el: HTMLElement
  private gardenScene: GardenScene
  private state: PanelState = { shape: null, visible: false }

  // DOM references
  private headerDot!: HTMLElement
  private titleEl!: HTMLElement
  private contentEl!: HTMLElement
  private actionsEl!: HTMLElement

  // Cleanup callbacks
  private unsubUndo: (() => void) | null = null
  private unsubZoneState: (() => void) | null = null
  private unsubObjectState: (() => void) | null = null

  // Field registry
  private static fieldRenderers: Record<string, FieldRenderer> = {}

  // Track active input editing
  private activeInput: HTMLInputElement | null = null

  // Multi-select delta state
  private multiSelectDelta: MultiSelectDelta = { dx: 0, dy: 0, dRot: 0 }
  private multiSelectDeltaInputs: { dxInput: HTMLInputElement | null; dyInput: HTMLInputElement | null; dRotInput: HTMLInputElement | null } = { dxInput: null, dyInput: null, dRotInput: null }

  constructor(container: HTMLElement, gardenScene: GardenScene) {
    this.gardenScene = gardenScene

    // Create panel DOM
    this.el = document.createElement('div')
    this.el.className = 'floating-props-panel'
    this.buildDOM()
    container.appendChild(this.el)

    // Register default field renderers
    FloatingPropsPanel.registerFieldRenderer('zone', renderZoneFields)
    FloatingPropsPanel.registerFieldRenderer('groundShape', renderGroundShapeFields)
    FloatingPropsPanel.registerFieldRenderer('building', renderBuildingFields)
    FloatingPropsPanel.registerFieldRenderer('plant', renderPlantFields)
    FloatingPropsPanel.registerFieldRenderer('pin', renderPinFields)
    FloatingPropsPanel.registerFieldRenderer('boundary', renderBoundaryFields)

    // Subscribe to selection changes
    const selMgr = gardenScene.getSelectionManager()
    if (selMgr) {
      selMgr.setOnSelectionChange(this.onSelectionChange)
      selMgr.setOnMultiSelectionChange(this.onMultiSelectionChange)
      selMgr.setOnMultiSelectDelta(this.onMultiSelectDelta)
    }

    // Subscribe to undo/redo to refresh values
    this.unsubUndo = undoStack.onChange(() => {
      if (this.state.shape) {
        this.refreshFields()
      }
    })

    // Subscribe to state changes for live updates (e.g. from dragging)
    this.unsubZoneState = zoneState.onChange(() => {
      if (this.state.shape?.sourceType === 'zone' || this.state.shape?.sourceType === 'pin') {
        this.refreshFields()
      }
    })
    this.unsubObjectState = gardenObjectState.onChange(() => {
      if (this.state.shape && this.state.shape.sourceType !== 'zone' &&
          this.state.shape.sourceType !== 'pin' && this.state.shape.sourceType !== 'boundary') {
        // Check if object still exists
        const obj = gardenObjectState.getObjectById(this.state.shape.id)
        if (!obj) {
          this.hide()
          return
        }
        this.refreshFields()
      }
    })

    debug.log('FloatingProps', 'Panel initialized')
  }

  static registerFieldRenderer(sourceType: string, renderer: FieldRenderer): void {
    FloatingPropsPanel.fieldRenderers[sourceType] = renderer
  }

  private buildDOM(): void {
    // Header
    const header = document.createElement('div')
    header.className = 'fp-header'

    this.headerDot = document.createElement('div')
    this.headerDot.className = 'fp-type-dot'

    this.titleEl = document.createElement('span')
    this.titleEl.className = 'fp-title'

    const closeBtn = document.createElement('button')
    closeBtn.className = 'fp-btn'
    closeBtn.innerHTML = '<i class="fa-solid fa-xmark"></i>'
    closeBtn.title = t('props.close')
    closeBtn.addEventListener('click', () => {
      this.gardenScene.getSelectionManager()?.deselect()
    })

    header.appendChild(this.headerDot)
    header.appendChild(this.titleEl)
    header.appendChild(closeBtn)
    this.el.appendChild(header)

    // Content area (layer-specific fields + shared fields)
    this.contentEl = document.createElement('div')
    this.contentEl.className = 'fp-content'
    this.el.appendChild(this.contentEl)

    // Action row (delete button)
    this.actionsEl = document.createElement('div')
    this.actionsEl.className = 'fp-actions'
    this.el.appendChild(this.actionsEl)
  }

  // --- Selection handling ---

  private isMultiSelectMode = false

  private multiSelectResults: SelectionResult[] = []

  private onMultiSelectionChange = (results: SelectionResult[] | null): void => {
    if (results && results.length > 1) {
      this.isMultiSelectMode = true
      this.multiSelectResults = results
      this.showMultiSelect(results)
    } else {
      this.isMultiSelectMode = false
      // Multi-selection cleared — hide only if no single selection active
      const selMgr = this.gardenScene.getSelectionManager()
      if (!selMgr?.getSelected()) {
        this.hide()
      }
    }
  }

  private onMultiSelectDelta = (delta: MultiSelectDelta): void => {
    this.multiSelectDelta = delta
    this.updateMultiSelectDeltaFields()
  }

  private onSelectionChange = (result: SelectionResult | null): void => {
    // Ignore single-select callbacks while multi-select is active
    if (this.isMultiSelectMode) return

    if (!result) {
      this.hide()
      return
    }

    // Create adapter based on selection type
    const shape = this.createAdapter(result)
    if (!shape) {
      this.hide()
      return
    }

    this.show(shape)
  }

  private createAdapter(result: SelectionResult): EditableShape | null {
    const obj = result.object
    if (obj.type === 'zone') {
      return new ZoneAdapter(obj as Zone)
    }
    if (obj.type === 'rectangle' || obj.type === 'ellipse' || obj.type === 'polygon' || obj.type === 'path') {
      const stateObj = gardenObjectState.getObjectById(obj.id)
      if (stateObj) return new GroundShapeAdapter(stateObj)
    }
    if (obj.type === 'building') {
      const stateObj = gardenObjectState.getObjectById(obj.id)
      if (stateObj) return new BuildingAdapter(stateObj)
    }
    if (obj.type === 'plant') {
      const stateObj = gardenObjectState.getObjectById(obj.id)
      if (stateObj) return new PlantAdapter(stateObj)
    }
    if (obj.type === 'pin') {
      return new PinAdapter(obj as ContextPin)
    }
    if (obj.type === 'boundary') {
      const viewer = getViewer()
      if (viewer) {
        return new BoundaryAdapter(
          () => viewer.getSchemeOutline(),
          (o) => viewer.setSchemeOutline(o),
          () => viewer.getGarden()?.rotation ?? 0,
          (deg) => viewer.setGardenRotation(deg),
        )
      }
    }
    return null
  }

  // --- Show/hide ---

  private show(shape: EditableShape): void {
    this.state.shape = shape
    this.state.visible = true

    // Set accent color (prefer adapter's accent for pin-type-specific colors)
    const adapterColor = shape.getAccentColor?.()
    const color = adapterColor
      ? `#${adapterColor.toString(16).padStart(6, '0')}`
      : ACCENT_COLORS[shape.sourceType] ?? '#4ecca3'
    this.headerDot.style.backgroundColor = color

    // Set title
    this.titleEl.textContent = this.getTitle(shape)
    this.titleEl.title = this.titleEl.textContent

    // Render fields
    this.renderFields(shape)

    // Render actions
    this.renderActions(shape)

    // Show panel
    this.el.classList.add('visible')
  }

  hide(): void {
    // Commit any active input
    if (this.activeInput) {
      this.activeInput.blur()
      this.activeInput = null
    }

    this.state.shape = null
    this.state.visible = false
    this.multiSelectDelta = { dx: 0, dy: 0, dRot: 0 }
    this.multiSelectDeltaInputs = { dxInput: null, dyInput: null, dRotInput: null }
    this.multiSelectResults = []
    this.el.classList.remove('visible')
  }

  private showMultiSelect(results: SelectionResult[]): void {
    const count = results.length
    this.state.shape = null
    this.state.visible = true

    // Multi-select accent color
    this.headerDot.style.backgroundColor = '#4ecca3'

    // Title: "{count} objects selected"
    this.titleEl.textContent = t('props.multiSelect.title').replace('{count}', String(count))
    this.titleEl.title = this.titleEl.textContent

    // Content: delta fields
    this.contentEl.innerHTML = ''
    this.multiSelectDelta = { dx: 0, dy: 0, dRot: 0 }

    const selMgr = this.gardenScene.getSelectionManager()

    // "Move by" group with dX / dY inputs
    const moveGroup = createFieldGroup(t('props.multiSelect.moveBy'))
    const moveRow = document.createElement('div')
    moveRow.className = 'fp-input-row'

    const dxGroup = createPrefixedNumberInput('dX', 0, 'm', (value) => {
      const diff = value - this.multiSelectDelta.dx
      if (diff !== 0) selMgr?.applyMultiSelectDelta(diff, 0, 0)
    }, this)
    const dxInput = dxGroup.querySelector('input') as HTMLInputElement
    this.multiSelectDeltaInputs.dxInput = dxInput
    moveRow.appendChild(dxGroup)

    const dyGroup = createPrefixedNumberInput('dY', 0, 'm', (value) => {
      const diff = value - this.multiSelectDelta.dy
      if (diff !== 0) selMgr?.applyMultiSelectDelta(0, diff, 0)
    }, this)
    const dyInput = dyGroup.querySelector('input') as HTMLInputElement
    this.multiSelectDeltaInputs.dyInput = dyInput
    moveRow.appendChild(dyGroup)

    moveGroup.appendChild(moveRow)
    this.contentEl.appendChild(moveGroup)

    // "Rotate by" group
    const rotGroup = createFieldGroup(t('props.multiSelect.rotateBy'))
    const rotRow = document.createElement('div')
    rotRow.className = 'fp-input-row'

    const dRotGroup = createPrefixedNumberInput('', 0, '°', (value) => {
      const diff = value - this.multiSelectDelta.dRot
      if (diff !== 0) selMgr?.applyMultiSelectDelta(0, 0, diff)
    }, this)
    const dRotInput = dRotGroup.querySelector('input') as HTMLInputElement
    this.multiSelectDeltaInputs.dRotInput = dRotInput
    rotRow.appendChild(dRotGroup)

    rotGroup.appendChild(rotRow)
    this.contentEl.appendChild(rotGroup)

    // Lock/unlock all button (in content area)
    const allLocked = results.every(r =>
      isObjectLocked(objectTypeToSourceType(r.object.type), r.object.id)
    )
    const lockBtn = document.createElement('button')
    lockBtn.className = 'fp-lock-btn' + (allLocked ? ' locked' : '')
    lockBtn.innerHTML = `<i class="fa-solid ${allLocked ? 'fa-lock' : 'fa-lock-open'}"></i> ${allLocked ? t('lock.unlockAll') : t('lock.lockAll')}`
    lockBtn.addEventListener('click', () => {
      const newLocked = !allLocked
      const states = results.map(r => ({
        sourceType: objectTypeToSourceType(r.object.type),
        id: r.object.id,
        wasLocked: isObjectLocked(objectTypeToSourceType(r.object.type), r.object.id),
      }))
      for (const s of states) {
        setObjectLocked(s.sourceType, s.id, newLocked)
      }
      undoStack.push({
        description: `${newLocked ? 'Lock' : 'Unlock'} ${states.length} objects`,
        undo: () => { for (const s of states) setObjectLocked(s.sourceType, s.id, s.wasLocked) },
        redo: () => { for (const s of states) setObjectLocked(s.sourceType, s.id, newLocked) },
      })
      this.showMultiSelect(this.multiSelectResults)
    })
    this.contentEl.appendChild(lockBtn)

    // Actions: Delete All
    this.actionsEl.innerHTML = ''
    const deleteBtn = this.createActionBtn(t('props.multiSelect.delete'), 'fa-trash-can')
    deleteBtn.classList.add('delete')
    deleteBtn.addEventListener('click', () => {
      // Simulate pressing Delete key to trigger group delete in SelectTool
      const event = new KeyboardEvent('keydown', { key: 'Delete', bubbles: true })
      document.dispatchEvent(event)
    })
    this.actionsEl.appendChild(deleteBtn)

    this.el.classList.add('visible')
  }

  private updateMultiSelectDeltaFields(): void {
    const { dxInput, dyInput, dRotInput } = this.multiSelectDeltaInputs
    // Skip if user is actively editing one of the delta inputs
    if (this.activeInput && (this.activeInput === dxInput || this.activeInput === dyInput || this.activeInput === dRotInput)) return
    if (dxInput) dxInput.value = formatNumber(this.multiSelectDelta.dx)
    if (dyInput) dyInput.value = formatNumber(this.multiSelectDelta.dy)
    if (dRotInput) dRotInput.value = formatNumber(this.multiSelectDelta.dRot)
  }

  // --- Title ---

  private getTitle(shape: EditableShape): string {
    switch (shape.sourceType) {
      case 'zone': {
        const zone = zoneState.getZoneById(shape.id)
        if (zone?.label) return zone.label
        const def = zone ? getZoneTypeDef(zone.zoneType) : null
        return (def ? t(def.labelKey, def.label) : null) ?? t('props.fallback.zone')
      }
      case 'groundShape': {
        const obj = gardenObjectState.getObjectById(shape.id)
        return obj?.label || obj?.productName || t('props.fallback.surface')
      }
      case 'building': {
        const obj = gardenObjectState.getObjectById(shape.id)
        return obj?.label || obj?.productName || t('props.fallback.building')
      }
      case 'plant': {
        const obj = gardenObjectState.getObjectById(shape.id)
        return obj?.label || obj?.productName || t('props.fallback.plant')
      }
      case 'pin': {
        const pin = zoneState.getPinById(shape.id)
        if (pin?.label) return pin.label
        const def = pin ? getPinTypeDef(pin.pinType) : null
        return (def ? t(def.labelKey, def.label) : null) ?? t('props.fallback.pin')
      }
      case 'boundary':
        return t('props.fallback.boundary')
      default:
        return t('props.fallback.object')
    }
  }

  // --- Field rendering ---

  private renderFields(shape: EditableShape): void {
    this.contentEl.innerHTML = ''

    // Layer-specific fields
    const renderer = FloatingPropsPanel.fieldRenderers[shape.sourceType]
    if (renderer) {
      renderer(shape, this.contentEl, this)
    }

    // Divider
    const divider = document.createElement('div')
    divider.className = 'fp-divider'
    this.contentEl.appendChild(divider)

    // Shared fields: Position + Rotation
    renderSharedFields(shape, this.contentEl, this)

    // Lock toggle (not for boundary) — rendered as inline content button
    if (shape.sourceType !== 'boundary') {
      const locked = isObjectLocked(shape.sourceType, shape.id)
      const lockBtn = document.createElement('button')
      lockBtn.className = 'fp-lock-btn' + (locked ? ' locked' : '')
      lockBtn.innerHTML = `<i class="fa-solid ${locked ? 'fa-lock' : 'fa-lock-open'}"></i> ${t(locked ? 'lock.unlock' : 'lock.lock')}`
      lockBtn.addEventListener('click', () => {
        const wasLocked = isObjectLocked(shape.sourceType, shape.id)
        setObjectLocked(shape.sourceType, shape.id, !wasLocked)
        undoStack.push({
          description: `${wasLocked ? 'Unlock' : 'Lock'} ${shape.id}`,
          undo: () => setObjectLocked(shape.sourceType, shape.id, wasLocked),
          redo: () => setObjectLocked(shape.sourceType, shape.id, !wasLocked),
        })
        this.refreshFields()
        const selMgr = this.gardenScene.getSelectionManager()
        const result = selMgr?.getSelectedResult()
        if (result) {
          const nowLocked = isObjectLocked(shape.sourceType, shape.id)
          this.gardenScene.getHighlightManager()?.showSelection(result, nowLocked ? 0xFFA726 : undefined)
        }
      })
      this.contentEl.appendChild(lockBtn)
    }
  }

  refreshFields(): void {
    if (!this.state.shape) return

    // Don't refresh if user is actively editing an input
    if (this.activeInput && document.activeElement === this.activeInput) return

    // Update title
    this.titleEl.textContent = this.getTitle(this.state.shape)
    this.titleEl.title = this.titleEl.textContent

    // Re-render fields
    this.renderFields(this.state.shape)
    this.renderActions(this.state.shape)
  }

  // --- Actions ---

  private renderActions(shape: EditableShape): void {
    this.actionsEl.innerHTML = ''

    // Z-order buttons for ground shapes
    if (shape.sourceType === 'groundShape') {
      const frontBtn = this.createActionBtn(t('props.bringToFront'), 'fa-arrow-up')
      frontBtn.addEventListener('click', () => this.bringToFront(shape))
      this.actionsEl.appendChild(frontBtn)

      const backBtn = this.createActionBtn(t('props.sendToBack'), 'fa-arrow-down')
      backBtn.addEventListener('click', () => this.sendToBack(shape))
      this.actionsEl.appendChild(backBtn)
    }

    // Delete button
    const deleteBtn = this.createActionBtn(t('props.delete'), 'fa-trash-can')
    deleteBtn.classList.add('delete')
    deleteBtn.addEventListener('click', () => this.deleteSelected())
    this.actionsEl.appendChild(deleteBtn)
  }

  private createActionBtn(label: string, icon: string): HTMLButtonElement {
    const btn = document.createElement('button')
    btn.className = 'fp-action-btn'
    btn.innerHTML = `<i class="fa-solid ${icon}"></i> ${label}`
    return btn
  }

  // --- Delete ---

  private deleteSelected(): void {
    const shape = this.state.shape
    if (!shape) return

    if (shape.sourceType === 'boundary') {
      const viewer = getViewer()
      if (viewer) {
        const outline = viewer.getSchemeOutline()
        if (outline) {
          const snapshot = { x: outline.x, y: outline.y, points: outline.points.map((p: { x: number; y: number }) => ({ ...p })) }
          this.gardenScene.getSelectionManager()?.deselect()
          viewer.clearSchemeOutline()
          undoStack.push({
            description: 'Delete boundary',
            undo: () => viewer.setSchemeOutline(snapshot),
            redo: () => viewer.clearSchemeOutline(),
          })
        }
      }
    } else if (shape.sourceType === 'zone') {
      const zone = zoneState.getZoneById(shape.id)
      if (zone) {
        const snapshot = { ...zone }
        this.gardenScene.getSelectionManager()?.deselect()
        zoneState.deleteZone(shape.id)
        undoStack.push({
          description: `Delete zone ${shape.id}`,
          undo: () => zoneState.restoreZone(snapshot),
          redo: () => zoneState.deleteZone(shape.id),
        })
      }
    } else if (shape.sourceType === 'pin') {
      const pin = zoneState.getPinById(shape.id)
      if (pin) {
        const snapshot = { ...pin }
        this.gardenScene.getSelectionManager()?.deselect()
        zoneState.deletePin(shape.id)
        undoStack.push({
          description: `Delete pin ${shape.id}`,
          undo: () => zoneState.restorePin(snapshot),
          redo: () => zoneState.deletePin(shape.id),
        })
      }
    } else {
      const obj = gardenObjectState.getObjectById(shape.id)
      if (obj) {
        const snapshot = { ...obj }
        this.gardenScene.getSelectionManager()?.deselect()
        gardenObjectState.deleteObject(shape.id)
        undoStack.push({
          description: `Delete ${shape.sourceType} ${shape.id}`,
          undo: () => gardenObjectState.restoreObject(snapshot),
          redo: () => gardenObjectState.deleteObject(shape.id),
        })
      }
    }
  }

  // --- Z-order ---

  private bringToFront(shape: EditableShape): void {
    const obj = gardenObjectState.getObjectById(shape.id)
    if (!obj) return

    const shapes = gardenObjectState.getGroundShapes()
    const others = shapes.filter(s => s.id !== shape.id)
    const maxZ = others.length > 0 ? Math.max(...others.map(s => s.zIndex)) : 0
    if (obj.zIndex > maxZ) return

    const oldZ = obj.zIndex
    const newZ = maxZ + 1
    gardenObjectState.updateObject(shape.id, { zIndex: newZ })
    undoStack.push({
      description: `Bring to front ${shape.id}`,
      undo: () => gardenObjectState.updateObject(shape.id, { zIndex: oldZ }),
      redo: () => gardenObjectState.updateObject(shape.id, { zIndex: newZ }),
    })
  }

  private sendToBack(shape: EditableShape): void {
    const obj = gardenObjectState.getObjectById(shape.id)
    if (!obj) return

    const shapes = gardenObjectState.getGroundShapes()
    const others = shapes.filter(s => s.id !== shape.id)
    const minZ = others.length > 0 ? Math.min(...others.map(s => s.zIndex)) : 0
    if (obj.zIndex < minZ) return

    const oldZ = obj.zIndex
    const newZ = minZ - 1
    gardenObjectState.updateObject(shape.id, { zIndex: newZ })
    undoStack.push({
      description: `Send to back ${shape.id}`,
      undo: () => gardenObjectState.updateObject(shape.id, { zIndex: oldZ }),
      redo: () => gardenObjectState.updateObject(shape.id, { zIndex: newZ }),
    })
  }

  // --- Input helper (used by field renderers) ---

  setActiveInput(input: HTMLInputElement | null): void {
    this.activeInput = input
  }

  getShape(): EditableShape | null {
    return this.state.shape
  }

  // --- Cleanup ---

  dispose(): void {
    this.hide()
    this.unsubUndo?.()
    this.unsubZoneState?.()
    this.unsubObjectState?.()
    this.el.remove()
  }
}

// =============================================================================
// Field renderers
// =============================================================================

function renderZoneFields(shape: EditableShape, container: HTMLElement, panel: FloatingPropsPanel): void {
  const zone = zoneState.getZoneById(shape.id)
  if (!zone) return

  // Zone type (read-only info)
  const def = getZoneTypeDef(zone.zoneType)
  const typeGroup = createFieldGroup(t('props.type'))
  const typeText = document.createElement('div')
  typeText.className = 'fp-info-text'
  typeText.textContent = t(def.labelKey, def.label)
  typeGroup.appendChild(typeText)
  container.appendChild(typeGroup)

  // Custom label (editable)
  const labelGroup = createFieldGroup(t('props.label'))
  const labelInput = createTextInput(zone.label ?? '', t('props.labelPlaceholder'), (value) => {
    zoneState.updateZone(zone.id, { label: value || undefined })
  }, panel)
  labelGroup.appendChild(labelInput)
  container.appendChild(labelGroup)

  // Elevation (editable)
  const elevGroup = createFieldGroup(t('props.elevation'))
  const elevRow = document.createElement('div')
  elevRow.className = 'fp-input-row'
  const elevInput = createNumberInput(zone.elevation, 'm', (value) => {
    const clamped = Math.max(0.01, Math.min(10, value))
    const oldVal = zone.elevation
    zoneState.updateZone(zone.id, { elevation: clamped })
    undoStack.push({
      description: `Change zone elevation ${zone.id}`,
      undo: () => zoneState.updateZone(zone.id, { elevation: oldVal }),
      redo: () => zoneState.updateZone(zone.id, { elevation: clamped }),
    })
  })
  elevRow.appendChild(elevInput)
  elevGroup.appendChild(elevRow)
  container.appendChild(elevGroup)

  // Area (read-only)
  const area = computeZoneArea(zone)
  if (area > 0) {
    const areaGroup = createFieldGroup(t('props.area'))
    const areaText = document.createElement('div')
    areaText.className = 'fp-info-text'
    areaText.textContent = formatArea(area)
    areaGroup.appendChild(areaText)
    container.appendChild(areaGroup)
  }
}

function renderGroundShapeFields(shape: EditableShape, container: HTMLElement, panel: FloatingPropsPanel): void {
  const obj = gardenObjectState.getObjectById(shape.id)
  if (!obj) return

  // Surface name (read-only, from product)
  if (obj.productName) {
    const nameGroup = createFieldGroup(t('props.surface'))
    const nameText = document.createElement('div')
    nameText.className = 'fp-info-text'
    nameText.textContent = obj.productName
    nameGroup.appendChild(nameText)
    container.appendChild(nameGroup)
  }

  // Custom label (editable)
  const labelGroup = createFieldGroup(t('props.label'))
  const labelInput = createTextInput(obj.label ?? '', t('props.labelPlaceholder'), (value) => {
    gardenObjectState.updateObject(shape.id, { label: value || undefined })
  }, panel)
  labelGroup.appendChild(labelInput)
  container.appendChild(labelGroup)

  // Elevation (editable)
  const elevGroup = createFieldGroup(t('props.elevation'))
  const elevRow = document.createElement('div')
  elevRow.className = 'fp-input-row'
  const elevInput = createNumberInput(obj.elevation * DPM_SCALE, 'm', (value) => {
    const oldVal = obj.elevation
    const dotsValue = value / DPM_SCALE
    gardenObjectState.updateObject(shape.id, { elevation: dotsValue })
    undoStack.push({
      description: `Change elevation ${shape.id}`,
      undo: () => gardenObjectState.updateObject(shape.id, { elevation: oldVal }),
      redo: () => gardenObjectState.updateObject(shape.id, { elevation: dotsValue }),
    })
  })
  elevRow.appendChild(elevInput)
  elevGroup.appendChild(elevRow)
  container.appendChild(elevGroup)

  // Path width (editable, only for path type)
  if (obj.type === 'path' && obj.pathWidth != null) {
    const pwGroup = createFieldGroup(t('props.pathWidth', 'Path width'))
    const pwRow = document.createElement('div')
    pwRow.className = 'fp-input-row'
    const pwInput = createNumberInput(obj.pathWidth * DPM_SCALE, 'm', (value) => {
      const clamped = Math.max(0.1, Math.min(20, value))
      const oldWidth = obj.pathWidth!
      const newWidth = clamped / DPM_SCALE
      gardenObjectState.updateObject(shape.id, { pathWidth: newWidth })
      // Regenerate outline from waypoints with new width
      if (obj.pathWaypoints && obj.pathWaypoints.length >= 2) {
        const outline = generatePathPolygon(obj.pathWaypoints, newWidth)
        const xs = outline.map(p => p.x)
        const ys = outline.map(p => p.y)
        gardenObjectState.updateObject(shape.id, {
          points: outline,
          width: Math.max(...xs) - Math.min(...xs),
          height: Math.max(...ys) - Math.min(...ys),
        })
      }
      undoStack.push({
        description: `Change path width ${shape.id}`,
        undo: () => {
          gardenObjectState.updateObject(shape.id, { pathWidth: oldWidth })
          if (obj.pathWaypoints && obj.pathWaypoints.length >= 2) {
            const outline = generatePathPolygon(obj.pathWaypoints, oldWidth)
            const xs = outline.map(p => p.x)
            const ys = outline.map(p => p.y)
            gardenObjectState.updateObject(shape.id, {
              points: outline,
              width: Math.max(...xs) - Math.min(...xs),
              height: Math.max(...ys) - Math.min(...ys),
            })
          }
        },
        redo: () => {
          gardenObjectState.updateObject(shape.id, { pathWidth: newWidth })
          if (obj.pathWaypoints && obj.pathWaypoints.length >= 2) {
            const outline = generatePathPolygon(obj.pathWaypoints, newWidth)
            const xs = outline.map(p => p.x)
            const ys = outline.map(p => p.y)
            gardenObjectState.updateObject(shape.id, {
              points: outline,
              width: Math.max(...xs) - Math.min(...xs),
              height: Math.max(...ys) - Math.min(...ys),
            })
          }
        },
      })
      panel.refreshFields()
    })
    pwRow.appendChild(pwInput)
    pwGroup.appendChild(pwRow)
    container.appendChild(pwGroup)
  }

  // Area (read-only)
  const area = computeObjectArea(obj)
  if (area > 0) {
    const areaGroup = createFieldGroup(t('props.area'))
    const areaText = document.createElement('div')
    areaText.className = 'fp-info-text'
    areaText.textContent = formatArea(area)
    areaGroup.appendChild(areaText)
    container.appendChild(areaGroup)
  }
}

function renderBuildingFields(shape: EditableShape, container: HTMLElement, panel: FloatingPropsPanel): void {
  const obj = gardenObjectState.getObjectById(shape.id)
  if (!obj) return

  // Product name (read-only, from product)
  if (obj.productName) {
    const nameGroup = createFieldGroup(t('props.product'))
    const nameText = document.createElement('div')
    nameText.className = 'fp-info-text'
    nameText.textContent = obj.productName
    nameGroup.appendChild(nameText)
    container.appendChild(nameGroup)
  }

  // Custom label (editable)
  const labelGroup = createFieldGroup(t('props.label'))
  const labelInput = createTextInput(obj.label ?? '', t('props.labelPlaceholder'), (value) => {
    gardenObjectState.updateObject(shape.id, { label: value || undefined })
  }, panel)
  labelGroup.appendChild(labelInput)
  container.appendChild(labelGroup)

  // Dimensions (read-only)
  const dimGroup = createFieldGroup(t('props.dimensions'))
  const dimText = document.createElement('div')
  dimText.className = 'fp-info-text'
  const wM = (obj.width * DPM_SCALE).toFixed(2)
  const hM = (obj.height * DPM_SCALE).toFixed(2)
  dimText.textContent = `${wM} × ${hM} m`
  dimGroup.appendChild(dimText)
  container.appendChild(dimGroup)
}

function renderPlantFields(shape: EditableShape, container: HTMLElement, panel: FloatingPropsPanel): void {
  const obj = gardenObjectState.getObjectById(shape.id)
  if (!obj) return

  // Species name (read-only, from product) + info button
  if (obj.productName) {
    const nameGroup = createFieldGroup(t('props.species'))
    const nameRow = document.createElement('div')
    nameRow.style.display = 'flex'
    nameRow.style.alignItems = 'center'
    nameRow.style.gap = '6px'

    const nameText = document.createElement('div')
    nameText.className = 'fp-info-text'
    nameText.style.flex = '1'
    nameText.textContent = obj.productName
    nameRow.appendChild(nameText)

    // Info button (only if product code is available)
    const viewer = getViewer()
    const productCode = obj.productId ? viewer?.getProductCode(obj.productId) : undefined
    if (productCode) {
      const infoBtn = document.createElement('button')
      infoBtn.className = 'fp-plant-info-btn'
      infoBtn.title = t('plantInfo.infoButton')
      infoBtn.innerHTML = '<i class="fa-solid fa-circle-info"></i>'
      infoBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        import('./PlantInfoModal').then(m => m.plantInfoModal.open({
          productCode,
          productId: obj.productId,
          productName: obj.productName,
        }))
      })
      nameRow.appendChild(infoBtn)
    }

    nameGroup.appendChild(nameRow)
    container.appendChild(nameGroup)
  }

  // Custom label (editable)
  const labelGroup = createFieldGroup(t('props.label'))
  const labelInput = createTextInput(obj.label ?? '', t('props.labelPlaceholder'), (value) => {
    gardenObjectState.updateObject(shape.id, { label: value || undefined })
  }, panel)
  labelGroup.appendChild(labelInput)
  container.appendChild(labelGroup)

  // Dimensions (read-only)
  const dimGroup = createFieldGroup(t('props.dimensions'))
  const dimText = document.createElement('div')
  dimText.className = 'fp-info-text'
  const wM = (obj.width * DPM_SCALE).toFixed(2)
  const hM = (obj.height * DPM_SCALE).toFixed(2)
  dimText.textContent = `${wM} × ${hM} m`
  dimGroup.appendChild(dimText)
  container.appendChild(dimGroup)
}

function renderPinFields(shape: EditableShape, container: HTMLElement, panel: FloatingPropsPanel): void {
  const pin = zoneState.getPinById(shape.id)
  if (!pin) return

  // Pin type (read-only info)
  const def = getPinTypeDef(pin.pinType)
  const typeGroup = createFieldGroup(t('props.type'))
  const typeText = document.createElement('div')
  typeText.className = 'fp-info-text'
  typeText.textContent = t(def.labelKey, def.label)
  typeGroup.appendChild(typeText)
  container.appendChild(typeGroup)

  // Custom label (editable)
  const labelGroup = createFieldGroup(t('props.label'))
  const labelInput = createTextInput(pin.label ?? '', t('props.labelPlaceholder'), (value) => {
    zoneState.updatePin(pin.id, { label: value || undefined })
  }, panel)
  labelGroup.appendChild(labelInput)
  container.appendChild(labelGroup)
}

function renderBoundaryFields(_shape: EditableShape, container: HTMLElement, _panel: FloatingPropsPanel): void {
  const viewer = getViewer()
  const outline = viewer?.getSchemeOutline()
  if (!outline) return

  // Vertex count (read-only)
  const vertGroup = createFieldGroup(t('props.boundaryVertices'))
  const vertText = document.createElement('div')
  vertText.className = 'fp-info-text'
  vertText.textContent = `${outline.points.length}`
  vertGroup.appendChild(vertText)
  container.appendChild(vertGroup)

  // Area (read-only)
  if (outline.points.length >= 3) {
    const area = calculatePolygonAreaFromPoints(outline.points)
    if (area > 0) {
      const areaGroup = createFieldGroup(t('props.area'))
      const areaText = document.createElement('div')
      areaText.className = 'fp-info-text'
      areaText.textContent = formatArea(area)
      areaGroup.appendChild(areaText)
      container.appendChild(areaGroup)
    }
  }

  // G12: Align horizontally button (only if alignment angle > 1°)
  if (outline.points.length >= 3 && viewer) {
    const angle = calculateAlignmentAngle(outline.points)
    if (Math.abs(angle) > 1) {
      const alignBtn = document.createElement('button')
      alignBtn.className = 'fp-action-btn'
      alignBtn.innerHTML = `<i class="fa-solid fa-arrows-left-right"></i> ${t('props.alignBoundary')}`
      alignBtn.addEventListener('click', () => {
        const beforeOutline = viewer.getSchemeOutline()
        const garden = viewer.getGarden()
        const rotBefore = garden?.rotation ?? 0

        viewer.rotateBoundary(angle)

        const afterOutline = viewer.getSchemeOutline()
        if (beforeOutline && afterOutline) {
          const beforeSnap = { x: beforeOutline.x, y: beforeOutline.y, points: beforeOutline.points.map(p => ({ ...p })) }
          const afterSnap = { x: afterOutline.x, y: afterOutline.y, points: afterOutline.points.map(p => ({ ...p })) }
          const rotAfter = garden?.rotation ?? 0
          undoStack.push({
            description: 'Align boundary horizontally',
            undo: () => {
              viewer.setSchemeOutline(beforeSnap)
              const g = viewer.getGarden()
              if (g) {
                g.rotation = rotBefore
                viewer.getScene()?.setSunDirection(rotBefore)
              }
            },
            redo: () => {
              viewer.setSchemeOutline(afterSnap)
              const g = viewer.getGarden()
              if (g) {
                g.rotation = rotAfter
                viewer.getScene()?.setSunDirection(rotAfter)
              }
            },
          })
        }
      })
      container.appendChild(alignBtn)
    }
  }
}

// =============================================================================
// Shared fields (position, rotation) — appended for all sourceTypes
// =============================================================================

function renderSharedFields(shape: EditableShape, container: HTMLElement, panel: FloatingPropsPanel): void {
  const center = shape.getCenter()
  const rotation = shape.getRotation()

  // Position
  const posGroup = createFieldGroup(t('props.position'))
  const posRow = document.createElement('div')
  posRow.className = 'fp-input-row'

  posRow.appendChild(createPrefixedNumberInput('X', center.x * DPM_SCALE, 'm', (value) => {
    const editorX = value / DPM_SCALE
    const currentCenter = shape.getCenter()
    shape.beginEdit()
    shape.moveTo(editorX, currentCenter.y)
    shape.commit()
  }, panel))

  posRow.appendChild(createPrefixedNumberInput('Y', center.y * DPM_SCALE, 'm', (value) => {
    const editorY = value / DPM_SCALE
    const currentCenter = shape.getCenter()
    shape.beginEdit()
    shape.moveTo(currentCenter.x, editorY)
    shape.commit()
  }, panel))

  posGroup.appendChild(posRow)
  container.appendChild(posGroup)

  // Rotation
  const rotGroup = createFieldGroup(t('props.rotation'))
  const rotRow = document.createElement('div')
  rotRow.className = 'fp-input-row'
  rotRow.appendChild(createPrefixedNumberInput('', rotation, '°', (value) => {
    shape.beginEdit()
    shape.rotate(value)
    shape.commit()
  }, panel))

  // Rotate 90° clockwise button
  const rot90Btn = document.createElement('button')
  rot90Btn.className = 'fp-icon-btn'
  rot90Btn.title = t('props.rotate90')
  rot90Btn.innerHTML = '<i class="fa-solid fa-rotate-right"></i>'
  rot90Btn.addEventListener('click', () => {
    const currentRot = shape.getRotation()
    shape.beginEdit()
    shape.rotate(currentRot + 90)
    shape.commit()
    panel.refreshFields()
  })
  rotRow.appendChild(rot90Btn)

  rotGroup.appendChild(rotRow)
  container.appendChild(rotGroup)
}

// =============================================================================
// DOM helpers
// =============================================================================

function createFieldGroup(label: string): HTMLElement {
  const group = document.createElement('div')
  group.className = 'fp-field-group'
  const labelEl = document.createElement('div')
  labelEl.className = 'fp-field-label'
  labelEl.textContent = label
  group.appendChild(labelEl)
  return group
}

function createTextInput(
  value: string,
  placeholder: string,
  onChange: (value: string) => void,
  panel: FloatingPropsPanel
): HTMLInputElement {
  const input = document.createElement('input')
  input.type = 'text'
  input.className = 'fp-input'
  input.value = value
  input.placeholder = placeholder

  input.addEventListener('focus', () => panel.setActiveInput(input))
  input.addEventListener('blur', () => {
    panel.setActiveInput(null)
    onChange(input.value)
  })
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') input.blur()
    if (e.key === 'Escape') {
      input.value = value
      input.blur()
    }
    e.stopPropagation()
  })

  return input
}

function createNumberInput(
  value: number,
  suffix: string,
  onChange: (value: number) => void
): HTMLElement {
  const group = document.createElement('div')
  group.className = 'fp-input-group'

  const input = document.createElement('input')
  input.type = 'text'
  input.className = 'fp-input'
  input.value = formatNumber(value)

  const suffixEl = document.createElement('span')
  suffixEl.className = 'fp-input-suffix'
  suffixEl.textContent = suffix

  input.addEventListener('blur', () => {
    const parsed = parseLocaleNumber(input.value)
    if (isNaN(parsed)) {
      input.value = formatNumber(value)
      input.classList.remove('invalid')
    } else {
      input.classList.remove('invalid')
      onChange(parsed)
    }
  })

  input.addEventListener('input', () => {
    const parsed = parseLocaleNumber(input.value)
    input.classList.toggle('invalid', isNaN(parsed) && input.value.length > 0)
  })

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') input.blur()
    if (e.key === 'Escape') {
      input.value = formatNumber(value)
      input.classList.remove('invalid')
      input.blur()
    }
    e.stopPropagation()
  })

  group.appendChild(input)
  group.appendChild(suffixEl)
  return group
}

function createPrefixedNumberInput(
  prefix: string,
  value: number,
  suffix: string,
  onChange: (value: number) => void,
  panel: FloatingPropsPanel
): HTMLElement {
  const group = document.createElement('div')
  group.className = 'fp-input-group'

  if (prefix) {
    const prefixEl = document.createElement('span')
    prefixEl.className = 'fp-input-prefix'
    prefixEl.textContent = prefix
    group.appendChild(prefixEl)
  }

  const input = document.createElement('input')
  input.type = 'text'
  input.className = 'fp-input'
  input.value = formatNumber(value)

  const suffixEl = document.createElement('span')
  suffixEl.className = 'fp-input-suffix'
  suffixEl.textContent = suffix

  input.addEventListener('focus', () => panel.setActiveInput(input))
  input.addEventListener('blur', () => {
    panel.setActiveInput(null)
    const parsed = parseLocaleNumber(input.value)
    if (isNaN(parsed)) {
      input.value = formatNumber(value)
      input.classList.remove('invalid')
    } else {
      input.classList.remove('invalid')
      onChange(parsed)
    }
  })

  input.addEventListener('input', () => {
    const parsed = parseLocaleNumber(input.value)
    input.classList.toggle('invalid', isNaN(parsed) && input.value.length > 0)
  })

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') input.blur()
    if (e.key === 'Escape') {
      input.value = formatNumber(value)
      input.classList.remove('invalid')
      input.blur()
    }
    e.stopPropagation()
  })

  group.appendChild(input)
  group.appendChild(suffixEl)
  return group
}

function formatNumber(n: number): string {
  const str = Number.isInteger(n) ? n.toString() : n.toFixed(2)
  // Use locale-aware decimal separator (e.g. comma for Polish)
  if (getCurrentLocale() === 'pl') return str.replace('.', ',')
  return str
}

/** Parse a number string, accepting both '.' and ',' as decimal separator */
function parseLocaleNumber(s: string): number {
  return parseFloat(s.replace(',', '.'))
}

// =============================================================================
// Area computation helpers
// =============================================================================

function computeZoneArea(zone: Zone): number {
  if (zone.points && zone.points.length >= 3) {
    return calculatePolygonAreaFromPoints(zone.points)
  }
  if (zone.shapeKind === 'ellipse') {
    return calculateEllipseArea(zone.width, zone.height)
  }
  return calculateRectArea(zone.width, zone.height)
}

function computeObjectArea(obj: { type: string; width: number; height: number; points?: Array<{ x: number; y: number }> }): number {
  if (obj.points && obj.points.length >= 3) {
    return calculatePolygonAreaFromPoints(obj.points)
  }
  if (obj.type === 'ellipse') {
    return calculateEllipseArea(obj.width, obj.height)
  }
  return calculateRectArea(obj.width, obj.height)
}
